import React, { FC, useState, useEffect } from 'react';
import styles from './PinModal.module.scss';
import { useTranslation } from 'react-i18next';
import xmark from '../../../assets/icons/xmark.svg';
import eyeIcon from '../../../assets/img/eye_icon.svg';
import eyeOffIcon from '../../../assets/img/eye_off.svg';
import { usePrefetchQuery } from '../../movies/movieDetails/query';
import { fetchContractIds, saveContractId } from '../../../utils/refetch';
import parse from 'html-react-parser';

interface PinModalProps {
  confirmLogin: (pin: string) => void;
  error?: string;
}

const PinModal: FC<PinModalProps> = ({ confirmLogin, error }) => {
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const firstButtonRef = React.useRef<HTMLButtonElement>(null);
  const secondButtonRef = React.useRef<HTMLButtonElement>(null);
  const contractIDs = fetchContractIds();
  const [selectedContractId, setSelectedContractId] = useState<string | null>(contractIDs.length === 1 ? contractIDs[0] : null);


  const [pin, setPin] = useState<string>('');
  const [showPin, setShowPin] = useState<boolean>(false);
  const pinRef = React.useRef<string>('');
  pinRef.current = pin;

  const { prefetchData } = usePrefetchQuery();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value.replace(/[^0-9]/g, '');
    setPin(value);
  };

  useEffect(() => {
    if (pin.length === 4 && selectedContractId != null) {
      secondButtonRef.current?.focus();
    }
  }, [pin, selectedContractId]);


  const isAtString = () => {
    const localCountryId = localStorage.getItem('country_id');
    const prefetchCountryCode = prefetchData?.user_country_code;
    if (localCountryId) {
      return localCountryId === 'AT' ? 'Sky' : '18+';
    }
    return prefetchCountryCode === 'AT' ? 'Sky' : '18+';
  };

  return (
    <div className={`${styles.modalContainer} ${contractIDs.length > 1 ? styles.wide : ''}`}>
      <h1>{t('loginModal.title', { pin: isAtString() })}</h1>
      <h2>{t('loginModal.underTitle', { pin: isAtString() })}</h2>
      {contractIDs.length > 1 && <p>{t('loginModal.command')}</p>}
      {contractIDs.length > 1 && <div className={styles.contractSelection}>
        <div className={styles.radioContainers}>
          {contractIDs.map((contractId) => (
            <div key={contractId} className={styles.radioBox}>
              <input
                type="radio"
                id={contractId}
                name="contract"
                checked={selectedContractId === contractId}
                value={contractId}
                onChange={(value) => {
                  setSelectedContractId(value.target.value);
                }}
              />
              <label className={styles.label} htmlFor={contractId}>{contractId}</label>
            </div>
          ))}
        </div>
      </div>}
      <div className={styles.inputContainer}>
        <div className={styles.inputWrapper}>
          <label className={`${error ? styles.errorLabel : ''}`}
                 htmlFor="skyPin">* {prefetchData?.user_country_code === 'AT' || localStorage.getItem('country_id') == 'AT' ? 'Sky PIN' : '18+ PIN'}</label>
          <div className={styles.inputGroup}>
            <input autoComplete={'off'} className={`${error ? styles.errorPin : ''} ${styles.inputWithIcon}`}
                   onChange={handleInputChange} ref={inputRef}
                   id="skyPin"
                   title={prefetchData?.user_country_code === 'AT' || localStorage.getItem('country_id') == 'AT' ? 'Sky PIN' : '18+ PIN'}
                   type={showPin ? 'text' : 'password'}
                   maxLength={4}
                   value={pin}
                   onInput={(e) => e.currentTarget.value = e.currentTarget.value.replace(/[^0-9]/g, '')}
            />
            <img 
              src={showPin ? eyeOffIcon : eyeIcon}
              alt={showPin ? 'Hide PIN' : 'Show PIN'}
              className={styles.toggleIcon}
              onClick={() => setShowPin(!showPin)}
              title={showPin ? 'Hide PIN' : 'Show PIN'}
            />
          </div>
          {error && <p className={styles.errorMessage}><img src={xmark} alt="Error" /> {parse(error)}</p>}
        </div>
      </div>
      <div className={styles.buttonContainer}>
        <button ref={firstButtonRef} onClick={() => {
          localStorage.clear();
          window.location.href = '/';
        }}>{t('loginModal.back')}</button>
        <button ref={secondButtonRef}
                disabled={pin.length !== 4 || (selectedContractId == null && contractIDs.length > 1)}
                onClick={() => {
                  saveContractId(selectedContractId!);
                  confirmLogin(inputRef!.current!.value);
                }}>{t('loginModal.in')}</button>
      </div>
    </div>
  );
};

export default PinModal;
